import { useEffect, useRef, useState, type TouchEvent } from "react";
import { LayoutGrid, MessagesSquare } from "lucide-react";
import { useApp, useWide } from "../../context";
import { ChatHost } from "../chat/ChatHost";
import { FeatureSheets } from "./FeatureSheets";
import { Sidebar } from "../sidebar/Sidebar";
import { WorkspaceTab } from "../workspace/WorkspaceTab";
import { Onboarding } from "../onboarding/Onboarding";
import { Sheet } from "../../ui/components";
import "./main.css";

/**
 * 主界面。
 * - 手机：和 iOS 一样，对话页左侧是抽屉（左边缘右滑打开，点遮罩/左滑关闭），底部是浮动的“对话 / 工作台”导航
 * - 电脑：侧边栏常驻左侧，“对话 / 工作台”放在侧边栏顶部
 */
export function MainLayout() {
  const app = useApp();
  const wide = useWide();
  const keyboard = useKeyboardOpen();

  return (
    <div className={`main ${wide ? "wide" : "narrow"}${keyboard || app.selectedTab !== "chat" ? " no-tabbar-space" : ""}`}>
      {wide ? (
        <>
          <aside className="main-sidebar"><Sidebar showTabs /></aside>
          <section className="main-content">
            {app.selectedTab === "chat" ? <ChatHost /> : <WorkspaceTab />}
          </section>
        </>
      ) : (
        <>
          {app.selectedTab === "chat" ? <Drawer /> : <section className="main-content"><WorkspaceTab /></section>}
          {!app.isSidebarOpen && !keyboard && <TabBar />}
        </>
      )}
      <FeatureSheets />
      <Sheet open={app.showReauth} onClose={() => app.dismissConnect()} leading={<button className="sheet-done" onClick={() => app.dismissConnect()}>取消</button>}>
        <Onboarding mode={app.connectMode} />
      </Sheet>
    </div>
  );
}

/** 底部导航：对话 / 工作台（浮动的玻璃胶囊） */
function TabBar() {
  const app = useApp();
  const tabs = [
    { id: "chat" as const, title: "对话", icon: <MessagesSquare size={24} strokeWidth={1.8} /> },
    { id: "workspace" as const, title: "工作台", icon: <LayoutGrid size={24} strokeWidth={1.8} /> },
  ];
  return (
    <nav className="tab-bar" aria-label="主导航">
      {tabs.map((t) => (
        <button key={t.id} className={app.selectedTab === t.id ? "on" : ""} onClick={() => app.setTab(t.id)} aria-current={app.selectedTab === t.id}>
          {t.icon}
          <span>{t.title}</span>
        </button>
      ))}
    </nav>
  );
}

/** 手机上的对话页：对话 + 左侧抽屉 */
function Drawer() {
  const app = useApp();
  const [drag, setDrag] = useState(0);
  const start = useRef<{ x: number; y: number; open: boolean; horizontal?: boolean } | null>(null);
  const width = Math.min(window.innerWidth * 0.84, 360);
  const offset = Math.max(0, Math.min(width, (app.isSidebarOpen ? width : 0) + drag));
  const dragging = drag !== 0;

  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    // 关着的时候只认从左边缘开始的滑动，免得和消息里的横向滚动（代码块、表格）打架
    if (!app.isSidebarOpen && t.clientX > 28) { start.current = null; return; }
    start.current = { x: t.clientX, y: t.clientY, open: app.isSidebarOpen };
  };
  const onTouchMove = (e: TouchEvent) => {
    const s = start.current;
    if (!s) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (s.horizontal === undefined) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      s.horizontal = Math.abs(dx) > Math.abs(dy) * 1.5;
    }
    if (!s.horizontal) return;
    setDrag(s.open ? Math.min(0, dx) : Math.max(0, dx));
  };
  const onTouchEnd = () => {
    const s = start.current;
    start.current = null;
    if (!s?.horizontal) { setDrag(0); return; }
    if (s.open && drag < -width * 0.3) app.setSidebarOpen(false);
    else if (!s.open && drag > width * 0.3) app.setSidebarOpen(true);
    setDrag(0);
  };

  return (
    <div className="drawer" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}>
      <div className="drawer-side" style={{ width, transform: `translateX(${(offset - width) * 0.25}px)`, transition: dragging ? "none" : undefined }}
        aria-hidden={!app.isSidebarOpen}>
        <Sidebar />
      </div>
      <div
        className={`drawer-main${offset > 0 ? " shifted" : ""}`}
        style={{ transform: `translateX(${offset}px)`, transition: dragging ? "none" : undefined }}
      >
        <ChatHost />
        {offset > 0 && (
          <div className="drawer-scrim" style={{ opacity: (0.35 * offset) / width / 0.35 }} onClick={() => app.setSidebarOpen(false)} />
        )}
      </div>
    </div>
  );
}

/** 手机上弹出键盘时（输入框获得焦点）收起底部导航，不让它顶在键盘上面 */
function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const coarse = matchMedia("(pointer: coarse)").matches;
    if (!coarse) return;
    const isField = (el: EventTarget | null) => el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && !["checkbox", "radio", "button", "file"].includes(el.type));
    const onIn = (e: FocusEvent) => { if (isField(e.target)) setOpen(true); };
    const onOut = () => setTimeout(() => setOpen(isField(document.activeElement)), 50);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => { document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
  }, []);
  return open;
}
